import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { RiFocus3Line } from 'react-icons/ri';

import Attention from '@lib/core/src/components/Attention';
import palette from '@lib/core/src/theme/palette';
import { styled, TextField } from '@mui/material';

import { Card } from '@/components/AiWorkflow/AiWorkflowStyles';
import { DEFAULT_TIME_RANGE } from '@/components/AiWorkflow/formSchema';

const Container = styled(Card)({
  gap: 20,
  display: 'flex',
  flexDirection: 'column',
  '& .predefined-prompt': {
    color: palette.red[100]
  }
});

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16
});

const IconBox = styled('div')({
  width: 44,
  height: 44,
  borderRadius: 24,
  background: palette.backgrounds[200],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const Title = styled('div')({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 16,
  color: palette.black
});

const Description = styled('div')({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 13,
  color: palette.grey[500]
});

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    fontFamily: 'Reddit Sans',
    fontWeight: 500,
    fontSize: 13,
    color: palette.black,
    background: palette.white,
    borderRadius: 8,
    minHeight: 80,
    padding: '12px 10px'
  },
  '& .MuiInputBase-input': {
    minHeight: 80,
    padding: 0,
    resize: 'vertical',
    overflow: 'auto'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${palette.grey[1200]}`
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Reddit Sans',
    fontWeight: 500,
    fontSize: 11,
    color: palette.grey[400]
  }
});

const Task = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext();

  const timeRange = watch('timeRange');

  useEffect(() => {
    if (timeRange !== DEFAULT_TIME_RANGE) {
      const predefinedPrompt = `\n📅 In your response, please refer to the relevant meetings`;
      const prev = watch('prompt');
      if (!prev.includes(predefinedPrompt)) {
        setValue('prompt', prev + ' ' + predefinedPrompt, {
          shouldValidate: true,
          shouldTouch: true
        });
      }
    }
  }, [timeRange, setValue]);

  return (
    <Container>
      <Header>
        <IconBox>
          <RiFocus3Line size={20} color={palette.orange[100]} />
        </IconBox>
        <div>
          <Title>Task</Title>
          <Description>What should this agent do?</Description>
        </div>
      </Header>
      <Attention
        title="Pro tip: "
        description="Be as specific and detailed as possible in your prompt. Tell the agent exactly what to do - whether it’s identifying issues, summarizing meetings, or suggesting actions. Add clear examples, formats, or criteria when needed."
      />
      <StyledTextField
        {...register('prompt')}
        name="prompt"
        label={typeof errors.prompt?.message === 'string' ? errors.prompt.message : 'Task prompt'}
        placeholder="Ex: Extract me key items from my meetings..."
        value={watch('prompt')}
        error={Boolean(errors.prompt)}
        multiline
        minRows={4}
        maxRows={8}
        variant="outlined"
        fullWidth
      />
    </Container>
  );
};

export default Task;
